import { memo, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { TriangleAlertIcon as CautionIcon } from '@air/design-icons';
import {
  contentCollectionAllUploadsCompletedSelector,
  contentCollectionInvalidFieldsSelector,
} from '@air/feature-content-collection/redux';
import { useShortUrlListCustomFields } from '@air/feature-custom-fields/hooks';
import { Banner } from '@air/primitive-banner';
import { useBreakpointsContext } from '@air/provider-media-query';
import { useShortIdContext } from '@air/provider-short-id';
import { cn } from '@air/utils-tailwind';

export type ContentCollectionBannerProps = {
  className?: string;
};

export const ContentCollectionBanner = memo(({ className }: ContentCollectionBannerProps) => {
  const { isAboveMediumScreen } = useBreakpointsContext();
  const invalidFields = useSelector(contentCollectionInvalidFieldsSelector);
  const allUploadsCompleted = useSelector(contentCollectionAllUploadsCompletedSelector);
  const { shortId } = useShortIdContext();
  const { data: shortUrlCustomFields, isLoading: isLoadingShortUrlCustomFields } = useShortUrlListCustomFields({
    shortId: shortId,
  });

  // Using state here because we want the banner to be sticky (e.g. not disappear if additional files are added)
  const [showBanner, setShowBanner] = useState(false);
  useEffect(() => {
    if (allUploadsCompleted) {
      setShowBanner(true);
    }
  }, [allUploadsCompleted]);

  if (!showBanner) {
    return null;
  }

  const hasInvalidFields = invalidFields.length > 0 && !isLoadingShortUrlCustomFields;

  if (hasInvalidFields) {
    const requiredFieldNames = invalidFields.reduce<string[]>((names, field) => {
      switch (field.type) {
        case 'description':
          names.push('Description');
          break;
        case 'custom_field': {
          const name = shortUrlCustomFields?.data.find(({ id }) => id === field.customFieldId)?.name;

          if (name) {
            names.push(name);
          }
          break;
        }
      }
      return names;
    }, []);

    return (
      <Banner
        className={cn('sticky top-0 z-10 mb-2 items-center md:static md:mt-4 md:mr-1 md:ml-5', className)}
        appearance="danger"
      >
        <span className="hidden items-center gap-2 md:flex">
          <CautionIcon size="sm" />
          Your files are ready to be submitted but require the following{' '}
          {requiredFieldNames.length > 1 ? 'fields' : 'field'}: {requiredFieldNames.join(', ')}
        </span>
        <span className="flex items-center justify-center gap-2 md:hidden">
          <CautionIcon size="sm" />
          Your files have required fields missing.
        </span>
      </Banner>
    );
  }

  // This banner is intended for mobile only
  if (!isAboveMediumScreen) {
    return (
      <Banner
        className={cn('sticky top-0 z-10 mb-2 items-center md:static md:mt-4 md:mr-1 md:ml-5', className)}
        appearance="warning"
      >
        <span className="flex items-center justify-center gap-2">
          <CautionIcon size="sm" />
          Your files have not been submitted yet.
        </span>
      </Banner>
    );
  }

  return null;
});

ContentCollectionBanner.displayName = 'ContentCollectionBanner';
